import axios from 'axios';
import appConfig from './config';

const BASE_URI = appConfig.REACT_APP_BASE_URL;

const client = axios.create({
  baseURL: BASE_URI,
  json: true,
  timeout: 15000,
});

// A 401 from the auth endpoint itself means the submitted username/password were
// wrong — there is no session to expire. Clearing tokens and reloading there threw
// away the rejection Login.js needs, so a mistyped password just blanked the form
// with no explanation. Matched on the request path (login posts to '/auth'; the
// deployed base URL makes that '/ajax/auth') and deliberately narrow: the
// query-bearing logout DELETE '/auth?token=...' is NOT exempt.
const AUTH_PATH = '/auth';
const isAuthRequest = (error) =>
  Boolean(error && error.config && typeof error.config.url === 'string' && error.config.url.endsWith(AUTH_PATH));

// Adopt the rotated access token the backend issues on expiry, and send the user back to
// login on a genuine 401 instead of looping on stale state.
client.interceptors.response.use(
  (response) => {
    const rotated = response.headers && response.headers['authorization-reset'];
    if (rotated) {
      sessionStorage.setItem('accessToken', rotated);
    }
    return response;
  },
  (error) => {
    // Error responses carry a rotated token too. Without adopting it, every later request
    // repeats the full refresh path instead of presenting a valid token.
    const rotated =
      error && error.response && error.response.headers && error.response.headers['authorization-reset'];
    if (rotated) {
      sessionStorage.setItem('accessToken', rotated);
    }
    if (error && error.response && error.response.status === 401 && !isAuthRequest(error)) {
      sessionStorage.removeItem('accessToken');
      sessionStorage.removeItem('refreshToken');
      localStorage.removeItem('refreshToken');
      // One redirect per 30s window. Several screens poll on intervals, so an expired session
      // yields a 401 per in-flight poll; reloading on each one restarts the app in a loop.
      const last = Number(sessionStorage.getItem('authRedirectAt') || 0);
      if (Date.now() - last > 30000) {
        sessionStorage.setItem('authRedirectAt', String(Date.now()));
        window.location.reload();
      }
    }
    return Promise.reject(error);
  }
);

export default class APIClient {
  constructor() {
    if (sessionStorage.getItem('refreshToken')) {
      this.refreshToken = sessionStorage.getItem('refreshToken');
    } else if (localStorage.getItem('refreshToken')) {
      this.refreshToken = localStorage.getItem('refreshToken');
    }
  }

  async login(username, password) {
    let response = this.perform('post', '/auth', { username: username, password: password }, 'login');
    return await response;
  }

  ////////////////////////////////////////////////
  ////             Requests                   ////
  ////////////////////////////////////////////////
  getConfiguration(config) {
    return this.perform('get', '/configuration', undefined, undefined, config);
  }
  getPumpHouses() {
    // The house list is a mode of pump_house_data, sharing that endpoint's permission grant.
    return this.perform('get', '/pump_house_data?houses=1');
  }
  getPumpHouseGallonsSummary(unitID) {
    // A mode of pump_house_data (like houses=1) so it shares that endpoint's permission grant.
    return this.perform('get', '/pump_house_data?gallonsSummary=1&unitID=' + unitID);
  }
  setConfiguration(data) {
    return this.perform('put', '/configuration', data);
  }

  updateSatellite(satellite) {
    return this.perform('put', '/satellites?ID=' + satellite.ID, satellite);
  }

  updateStation(station) {
    return this.perform('put', '/stations?ID=' + station.ID, station);
  }

  updateStationType(stationType) {
    return this.perform('put', '/stationTypes?ID=' + stationType.ID, stationType);
  }

  createNewStationType(stationType) {
    return this.perform('post', '/stationTypes', stationType);
  }

  getSavedSchedules() {
    return this.perform('get', '/schedules');
  }

  updateSavedSchedule(schedule) {
    return this.perform('put', '/schedules?ID=' + schedule.ID, schedule);
  }

  saveNewSchedule(schedule) {
    return this.perform('put', '/schedules', schedule);
  }

  commitSchedule() {
    return this.perform('post', '/commands', { function: 'commit' });
  }

  toggleStationOn(id) {
    return this.perform('post', '/commands', {
      function: 'toggle_station',
      station_id: parseInt(id),
      action: 'start',
    });
  }

  toggleStationOff(id) {
    return this.perform('post', '/commands', {
      function: 'toggle_station',
      station_id: parseInt(id),
      action: 'stop',
    });
  }
  reEnableStation(id) {
    return this.perform('post', '/commands', {
      function: 'toggle_station',
      station_id: parseInt(id),
      action: 'enable',
    });
  }

  getSystemStats() {
    return this.perform('get', '/systemStats');
  }

  getSatelliteCellStatuses() {
    return this.perform('get', '/satellite_cell_status');
  }

  getSatelliteCellHistory() {
    return this.perform('get', '/satellite_cell_history');
  }

  getSatelliteCellHistoryDetail(unitID) {
    return this.perform('get', `/satellite_cell_history?unitID=${unitID}`);
  }

  uploadFirmware(firmware) {
    return this.perform('post', '/firmware_upload', firmware);
  }

  getUserRole() {
    return this.perform('get', '/user_role');
  }

  getUserNotificationPreferences() {
    return this.perform('get', '/user_notifications');
  }

  setUserNotificationPreferences(data) {
    return this.perform('post', '/user_notifications', data);
  }

  getPumpHouseData() {
    return this.perform('get', '/pump_house_data');
  }

  ////////////////////////////////////////////////
  ////          Perform HTTP Request          ////
  ////////////////////////////////////////////////
  async perform(method, resource, data, type, config = {}) {
    let headerValues = {};

    switch (type) {
      case 'form':
        break;
      case 'admin':
        break;
      case 'login':
        let auth = 'Basic ' + btoa(data.username + ':' + data.password);
        headerValues.Authorization = auth;
        break;
      default:
        headerValues = {
          Authorization: `Bearer ${sessionStorage.getItem('accessToken')}`,
          'Authorization-Refresh': `Bearer ${this.refreshToken}`,
        };
        break;
    }

    return client({
      ...config,
      method,
      url: resource,
      data,
      headers: headerValues,
    }).then(function (response) {
      return response.data;
    });
  }

  // Get the current dashboard status including rain hold and schedule modification flags.
  // Rejects on error so callers keep their last-known state instead of a fabricated all-clear.
  getDashboardStatus(config) {
    return this.perform('get', '/dashboard_status', undefined, undefined, config);
  }

  //Set the rain hold status. Rejects on error so callers can roll back optimistic UI.
  setRainHoldStatus(active) {
    return this.perform('post', '/dashboard_status', { active })
        .then((response) => response.success);
  }

  /**
   * Initiates a test sequence for all stations under the specified satellite.
   *
   * @param {number|string} satelliteId - The ID of the satellite to test.
   * @param {number} duration - Duration in seconds for each station's test (default is 10).
   * @returns {Promise<Object>} A promise that resolves with the server's response object.
   *
   * Note: Errors are logged as they could be captured by sentry.
   */
  testSatellite(satelliteId, duration = 10) {
    const payload = {
      function: 'test_satellite',
      satellite_id: satelliteId,
      duration: duration,
    };

    return this.perform('post', '/test_sequence', payload)
        .then((res) => res)
        .catch((err) => {
          console.error('[Satellite Test] Error:', err);
          throw err;
        });
  }

  /**
   * Send multi-station toggle command with specified runtime
   */
  multiToggleStations(stationIds, runtime) {
    return this.perform('post', '/commands', {
      function: 'multi_toggle_stations',
      stations: stationIds,
      time: runtime
    });
  }

  async logout() {
    try {
      // Get refresh token to invalidate it on the server
      const refreshToken = this.refreshToken;

      if (refreshToken) {
        // calling the backend to invalidate the token
        await this.perform('delete', `/auth?token=${refreshToken}`, {}, 'logout');
      }

      return { success: true };
    } catch (error) {
      // Even if the server call fails, the local tokens should be cleared
      return { success: true };
    }
  }

  /**
   * Get system history with pagination or filtering
   */
  async getSystemHistory(limit = 20, offset = 0, filters = {}, config) {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
    });

    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.actionType) params.append('actionType', filters.actionType);
    if (filters.satelliteID) params.append('satelliteID', filters.satelliteID);

    const response = await this.perform('get', `/system_history?${params.toString()}`, undefined, undefined, config);
    return response;
  }

  /**
   * Get detailed information for a specific batch
   */
  async getBatchDetails(batchID) {
    const response = await this.perform('get', `/system_history?batchID=${batchID}`);
    return response;
  }
}